import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import LayoutWithAds from '@/features/dashboard/LayoutWithAds';
import CalculatorTemplate from '@/components/calculators/CalculatorTemplate';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

const FutureValueCalculator = () => {
  const [result, setResult] = useState(null);

  const initialValues = {
    initial_investment: 10000,
    interest_rate: 5,
    compounding_frequency: 'yearly',
    investment_period: 10
  };

  const inputFields = {
    initial_investment: { 
      type: 'currency', 
      defaultValue: initialValues.initial_investment 
    },
    interest_rate: { 
      type: 'percentage', 
      defaultValue: initialValues.interest_rate 
    },
    compounding_frequency: {
      type: 'select',
      options: [
        { value: 'daily', label: 'Daily' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
      ],
      defaultValue: initialValues.compounding_frequency
    },
    investment_period: { 
      type: 'number', 
      defaultValue: initialValues.investment_period 
    },
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleCalculate = (formData) => {
    const P = parseFloat(formData.initial_investment);
    const r = parseFloat(formData.interest_rate) / 100;
    const t = parseFloat(formData.investment_period);
    let n;

    switch (formData.compounding_frequency) {
      case 'daily':
        n = 365;
        break;
      case 'monthly':
        n = 12;
        break;
      case 'yearly':
      default:
        n = 1;
    }

    const FV = P * Math.pow((1 + r/n), n*t);
    
    const newResult = {
      futureValue: FV,
      interestEarned: FV - P,
      P: P,
      r: r,
      n: n,
      t: t,
      calculationFormula: `FV = ${P.toFixed(2)} \\times \\left( 1 + \\frac{${(r*100).toFixed(2)}\\%}{${n}} \\right)^{${n} \\times ${t}} = ${FV.toFixed(2)}`
    };
    
    console.log('Calculation performed:', { input: formData, result: newResult });
    setResult(newResult);
  };

  const infoContent = (
    <>
      <h3 className="text-xl font-semibold mb-2">Understanding Future Value</h3>
      <p>
        Future Value (FV) is the value of an asset or cash at a specified date in the future, based on an assumed growth rate. This calculator helps you determine how much your investment will be worth in the future.
      </p>
      <h4 className="text-lg font-semibold mt-4 mb-2">Key Concepts:</h4>
      <ul className="list-disc list-inside mt-1 mb-4">
        <li>Initial Investment (P): The amount you start with</li>
        <li>Interest Rate (r): The annual rate at which your investment grows</li>
        <li>Compounding Frequency (n): How often the interest is calculated and added to the principal</li>
        <li>Investment Period (t): The length of time you plan to hold the investment</li>
      </ul>
      <p className="mt-2">
        This calculator uses the compound interest formula to determine the future value of your investment. It's a powerful tool for financial planning and understanding the potential growth of your investments over time.
      </p>
    </>
  );

  const generalFormulaString = "FV = P \\times \\left( 1 + \\frac{r}{n} \\right)^{n \\times t}";

  return (
    <CalculatorTemplate
      title="Future Value Calculator"
      icon={Calculator}
      inputText="Find the future value of an investment of {initial_investment} at {interest_rate}% interest compounded {compounding_frequency} for {investment_period} years."
      inputFields={inputFields}
      initialValues={initialValues}
      onCalculate={handleCalculate}
      infoTitle="About Future Value"
      infoContent={infoContent}
      generalFormula={generalFormulaString}
      formulaDescription="Future Value Formula"
      result={result && (
        <div className="mt-4 p-4 bg-green-100 rounded-md">
          <h3 className="text-lg font-semibold text-green-800">Results:</h3>
          <p className="text-green-700">Future Value: {formatCurrency(result.futureValue)}</p>
          <p className="text-green-700">Interest Earned: {formatCurrency(result.interestEarned)}</p>
          <div className="mt-4">
            <h4 className="text-md font-semibold text-green-800">Calculation Breakdown:</h4>
            <p className="text-green-700 mt-2">
              Where:
              <br />P (Initial Investment) = {formatCurrency(result.P)}
              <br />r (Annual Interest Rate) = {(result.r * 100).toFixed(2)}%
              <br />n (Compounding Frequency) = {result.n} times per year
              <br />t (Investment Period) = {result.t} years
            </p>
            <div className="mt-2">
              <p className="text-green-700">Applying the formula:</p>
              <InlineMath math={result.calculationFormula} />
            </div>
          </div>
        </div>
      )}
    />
  );
};

FutureValueCalculator.getLayout = function getLayout(page) {
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Interest Calculators', path: '/interest-calculators' },
    { label: 'Future Value Calculator', path: '/interest-calculators/future-value' },
  ];

  return (
    <LayoutWithAds breadcrumbs={<Breadcrumbs items={breadcrumbItems} />}>
      {page}
    </LayoutWithAds>
  );
};

export default FutureValueCalculator;